/*
(C) 2020, 2021, 2022, 2023, 2024 EDISOFT


System : ESA Datalabs

File Name : sepp_ui/src/global/state.ts

Author : EDISOFT, CGI, ACRI-ST

Creation Date : 2020-04-21

This file is subject to the terms and conditions defined in 'LICENSE.txt',
which is part of this source code package.
No part of the package, including this file, may be copied, modified, propagated,
or distributed except according to the terms contained in 'LICENSE.txt'.
*/

import { api } from '../global/api'
import { appState, Logger, Datalab, Resource, IState, AuthenticationState, Datavol, Cluster, DataCentre, DatalabCategory, RunningState, ResourceState } from '@datalabs/edl_common_ui'

const logger = new Logger("service", "state")

export const ANNOUNCEMENT_UPDATED = 'announcementUpdated';
export const DATALAB_STATE_EVENT = 'datalabState';
export const ROLES_CHANGED_EVENT = 'rolesChanged';
export const DATALAB_RESOURCE_STATE_EVENT = 'resourceState';
export const FILE_MANAGER_UPDATED = 'fileManagerUpdated';
export const DATA_CENTRE_CHANGED = 'dataCentreChanged';
export const TEAMS_LIST_CHANGED = 'teamsListChanged'

export const FILE_BROWSER_NAME = 'file-browser';

const authQuery = `
query {
  getAuthenticationState {
    backends, username, user {
      displayName, roles {
        id
        description
        target
        permissions {
          id
          description
          target
        }
      }
    }
  }
}`;

const getUserRolesQuery = `
query getUser($userId: ID!) {
  getUser(userID: $userId) {
    roles {
      id
      description
      target
      permissions {
        id
        description
        target
      }
    }
  }
}`;

const dataCentresQuery = `
query {
  dataCentres {
    id
    displayName
    clusters {
      id
      displayName
      displayLabel
      default
      dataCentreId
    }
  }
}`;

const datalabsQuery = `
query datalabs($clusterId: ID) {
  datalabs(clusterId: $clusterId) {
    id name dlp { id name } secret categories state details clusterId
    resources { id name type categories specification state details }
  }
}`;

const dataVolumesQuery = `
query dataVolumes($clusterId: ID) {
  dataVolumes(clusterId: $clusterId) {
    dvId name path url dataCentreIds clusterIds automount
  }
}`;

const ensureFileBrowserMutation = `
mutation($clusterId: ID) {
  ensureFileBrowser(clusterId: $clusterId) {
    id
  }
}`;

const ALL = '*';

export class State implements IState {
  dataCentresListUpdated: CustomEvent;
  datalabsListUpdated: CustomEvent;
  datavolsListUpdated: CustomEvent;

  public devMode: Boolean = location.port == "3333" || location.host == 'localhost';
  public datalabBaseUrl: string = ""
  public authentication?: AuthenticationState = null;
  public cluster?: Cluster = null;
  public dataCentres: DataCentre[] = [];
  private clusters: Cluster[] = [];
  public datalabs: Datalab[] = [];
  public datavols: Datavol[] = [];
  public resources: Resource[] = [];
  public fileManagerState?: ResourceState = null;
  public fMgrIfId?: string = null;
  // lookup tables
  public datalabById = {};
  public datalabByResourceId = {};
  public resourceById = {};
  public systemDatalabByDlpId: { [key: string]: string } = {};
  public announcementBannerHeight: number = 0;

  constructor() {
    logger.debug(`devMode ${this.devMode}`);
    this.datalabsListUpdated = new CustomEvent("datalabsListUpdated");
    this.datavolsListUpdated = new CustomEvent("datavolsListUpdated");
    window.addEventListener('dataCentreSelected', (ev: CustomEvent) => this.dataCentreSelected(ev.detail));
    window.addEventListener('datalabCreating', (ev: CustomEvent) => this.datalabCreating(ev.detail));
    window.addEventListener('datalabCreated', (ev: CustomEvent) => this.datalabCreated(ev.detail));
    window.addEventListener('datalabDestroyed', (ev: CustomEvent) => this.datalabDestroyed(ev.detail));
    window.addEventListener(DATALAB_STATE_EVENT, (ev: CustomEvent) => this.datalabState(ev.detail));
    window.addEventListener('datavolumeCreated', (ev: CustomEvent) => this.datavolumeCreated(ev.detail));
    window.addEventListener('datavolumeUpdated', (ev: CustomEvent) => this.datavolumeUpdated(ev.detail));
    window.addEventListener('datavolumeDestroyed', (ev: CustomEvent) => this.datavolumeDestroyed(ev.detail));
    window.addEventListener(DATALAB_RESOURCE_STATE_EVENT, (ev: CustomEvent) => this.resourceState(ev.detail));
    window.addEventListener('rolesUpdated', () => this.rolesUpdated());
    setTimeout(() => this.getAuthenticationState(), 0);
  }

  getAuthenticationState() {
    api.call(authQuery).then(response => {
      this.authentication = (
        response.data
          ? response.data.getAuthenticationState
          : { backends: null }
      );
      logger.debug("emitting authenticationStateReceived", this.authentication);
      appState.user = this.authentication;
      window.dispatchEvent(new CustomEvent("authenticationStateReceived"));
      if (this.authentication.user) {
        api.subscribeToUserEvents(this.authentication.username);
        this.getDataCentres();
      }
    });
  }

  getDataCentres() {
    logger.info("getting data centres");
    api.call(dataCentresQuery).then(response => {
      this.dataCentres = response.data.dataCentres;
      this.clusters = this.dataCentres.reduce((clusters, dataCentre) => {
        clusters.push(...dataCentre.clusters);
        return clusters;
      }, []);
      window.dispatchEvent(new CustomEvent("dataCentresUpdated"));
      this.dataCentreSelected(localStorage.getItem("clusterId"));
    });
  }

  ensureFileBrowser(clusterId: string) {
    api.call(ensureFileBrowserMutation, {clusterId: clusterId}).then(response => {
      const id = response.data.ensureFileBrowser.id;
      logger.info("ensureFileBrowser", id);
      this.checkFileBrowserState();
    });
  }

  resourceState(resourceUpdate) {
    let res = this.resourceById[resourceUpdate.id];
    if (res === undefined) {
      res = resourceUpdate;
      this.resourceById[res.id] = res;
    } else {
      for (const [k, v] of Object.entries(resourceUpdate)) {
        res[k] = v;
      }
    }
    this.resourceById[`${res.dlId}-${res.type}-${res.name}`] = res;
    this.checkFileBrowserState();
  }

  rolesUpdated() {
    if (this.authentication) {
      api.call(getUserRolesQuery, {userId: this.authentication.username}).then(response => {
        const roles = response.data.getUser.roles;
        logger.info('roles updated', roles);
        this.authentication.user = { ...this.authentication.user, roles };
        window.dispatchEvent(new CustomEvent(ROLES_CHANGED_EVENT));
      });
    }
  }

  checkFileBrowserState() {
    const fileManager = this.datalabs.find(datalab =>
      datalab.categories & DatalabCategory.System
      && datalab.name == FILE_BROWSER_NAME
      && datalab.clusterId === this.cluster?.id
    );
    let fileManagerState = null;
    let fileManagerId = null;
    if (fileManager) {
      let resource = fileManager.resources[0];
      for (const res of fileManager.resources) {
        if (res.name == "main") {
          resource = res;
          break;
        }
      }
      fileManagerState = resource?.state;
      if (resource?.state === ResourceState.Ready) {
        fileManagerId = resource.id;
      }
    }
    if (this.fMgrIfId !== fileManagerId || fileManagerState && this.fileManagerState !== fileManagerState) {
      this.fileManagerState = fileManagerState;
      this.fMgrIfId = fileManagerId;
      logger.info("state.fMgrIfId", this.fMgrIfId);
      window.dispatchEvent(new CustomEvent(FILE_MANAGER_UPDATED));
    }
  }

  updateList() {
    for (let dl of this.datalabs) {
      this.datalabById[dl.id] = dl;
      for (let res of dl.resources) {
        this.resourceById[res.id] = res;
        this.datalabByResourceId[res.id] = dl.id;
        this.resourceById[`${dl.id}-${res.type}-${res.name}`] = res;
      }
      if (dl.name === FILE_BROWSER_NAME) {
        this.systemDatalabByDlpId["file-browser"] = dl.id;
        this.checkFileBrowserState();
      }
    }
    window.dispatchEvent(this.datalabsListUpdated);
  }

  getDatalabs(clusterId: string) {
    logger.info("getting datalabs");
    api.call(datalabsQuery, {clusterId: clusterId}).then(res => {
      logger.info("getDatalabs response", res)
      this.datalabs = res.data ? res.data.datalabs : [];
      this.updateList();
    });
  }

  getDataVolumes(clusterId: string) {
    logger.info("getting datavols");
    setTimeout(_ => {
      api.call(dataVolumesQuery, {clusterId: clusterId}).then(res => {
        logger.info("getDataVolumes response", res)
        this.datavols = res.data ? (res.data.dataVolumes ? res.data.dataVolumes : []) : [];
        this.datavols.forEach(element => {
          element["dvId"] ? element["id"] = element["dvId"] : null
        });
        logger.info("Datavolumes", this.datavols)
        window.dispatchEvent(this.datavolsListUpdated);
      });
    }, 100);
  }

  dataCentreSelected(clusterId?: string) {
    if (clusterId) {
      this.cluster = this.clusters.find((cluster) => cluster.id === clusterId);
    }
    if (!this.cluster) {
      this.cluster = this.clusters.find((cluster) => cluster.default);
    }
    localStorage.setItem("clusterId", this.cluster.id);
    this.getDataVolumes(this.cluster.id);
    this.getDatalabs(this.cluster.id);
    this.ensureFileBrowser(this.cluster.id);
    window.dispatchEvent(new CustomEvent(DATA_CENTRE_CHANGED));
  }

  datalabCreating(creatingDatalab: Datalab) {
    if (this.cluster?.id === creatingDatalab.clusterId) {
      this.updateDatalab(creatingDatalab);
      if (creatingDatalab.name === FILE_BROWSER_NAME && this.fileManagerState !== ResourceState.Waiting) {
        this.fileManagerState = ResourceState.Waiting;
        window.dispatchEvent(new CustomEvent(FILE_MANAGER_UPDATED));
      }
    }
  }

  datalabCreated(createdDatalab: Datalab) {
    if (this.cluster?.id === createdDatalab.clusterId) {
      this.updateDatalab(createdDatalab);
    }
  }

  updateDatalab(updatedDatalab: Datalab) {
    const existingDatalab = this.datalabs.find(datalab => datalab.id === updatedDatalab.id);
    if (existingDatalab) {
      // replace the existing datalab in the list
      this.datalabs = this.datalabs.map(datalab => datalab.id === updatedDatalab.id ? updatedDatalab : datalab);
    } else {
      // add the new datalab to the list
      this.datalabs = [...this.datalabs, updatedDatalab];
    }
    this.updateList();
}

  datalabDestroyed(dlId: string) {
    // remove the destroyed datalab from the list
    this.datalabs = this.datalabs.filter(datalab => datalab.id != dlId);
    this.updateList();
  }

  datalabState(data: { id: string, state: RunningState }) {
    for (var datalab of this.datalabs) {
      if (datalab.id == data.id) {
        datalab.state = data.state;
        this.updateList();
      }
    }
  }

  datavolumeCreated(data: Datavol) {
    if ((data.dataCentreIds.includes(ALL) || data.dataCentreIds.includes(this.cluster?.dataCentreId))
     && (data.clusterIds.includes(ALL) || data.clusterIds.includes(this.cluster?.id))) {
      // add the new datavolume to the list
      this.datavols = [...this.datavols, data];
      window.dispatchEvent(this.datavolsListUpdated);
    }
  }

  datavolumeDestroyed(dvId: string) {
    // remove the destroyed datavolume from the list
    this.datavols = this.datavols.filter(volume => volume.id != dvId);
    window.dispatchEvent(this.datavolsListUpdated);
  }

  datavolumeUpdated(data: Datavol) {
    // replace the existing datavolume in the list
    this.datavols = this.datavols.map(volume => volume.id === data.id ? data : volume);
    window.dispatchEvent(this.datavolsListUpdated);
  }
}
